/**
 * Per-chat state management for chat input functionality using signals (ELARA-952)
 *
 * One instance backs a single chat's composer — message text, attachments, answered questions, and
 * send state. Instances are created and keyed by chat id by {@link createChatInputStateManager}, so a
 * draft typed in one chat never leaks into another (the bug this replaced: a single global store whose
 * fields were wiped by a `reset()` that raced trip/chat switches). The serializable part of the draft
 * (text + answered questions + URL/data attachments) is persisted per-chat via the injected
 * {@link KeyValueStore} so an unsent draft survives an app reload.
 *
 * Key features:
 * 1. Message text management
 * 2. File attachment handling
 * 3. Drag and drop state
 * 4. Send operation coordination
 * 5. Input validation and constraints
 * 6. Per-chat draft persistence across reloads
 */

import type { DataAttachmentType } from '@elara/domain/models/schemas/attachment-schemas';
import type { DataPart, MediaPart, TextPart } from '@elara/domain/models/schemas/message-schemas';
import type { UserQuestion } from '@elara/domain/models/schemas/widget-schemas';

import type { Asset } from '#lib/asset.js';
import type { FilePicker } from '#lib/file-picker.js';
import type { ImageProcessor } from '#lib/image-processor.js';
import type { KeyValueStore } from '#lib/key-value-store.js';
import type { SpeechRecognitionPort, SpeechRecognitionSession } from '#lib/speech-recognition-port.js';
import type { Chat } from '#models/ChatModel.js';
import type { ChatsState } from './ChatsState.js';
import type { FileUploadState } from './FileUploadState.js';
import type { ModelState } from './ModelState.js';
import type { TripsState } from './TripsState.js';

import { effect } from '@preact/signals-react';
import { omit } from 'lodash-es';

import { errorMessage } from '@elara/core/errors';
import { MAX_ATTACHMENT_BYTES } from '@elara/domain/constants';

import { trackMessageSent } from '#lib/analytics-events.js';
import { isUnreachableFlowError } from '#lib/flow-utils.js';
import { createState } from '#lib/signals-utils.js';

import {
  clearChatDraft,
  DRAFT_SAVE_DEBOUNCE_MS,
  loadChatDraft,
  saveChatDraft,
} from './chat-draft-storage.js';

/**
 * Represents an attachment that is a URL (used for images that cannot be fetched due to CORS)
 */
export interface UrlAttachment {
  url: string;
  name: string;
  mimeType: string;
  size?: number;
  type: 'url';
}

/**
 * Represents a generic data attachment (destination, hotel, or any other structured data)
 */
export interface DataAttachment {
  type: DataAttachmentType;
  id: string; // The ID of the referenced entity
  displayName?: string; // Display name for UI
  data?: Record<string, unknown>; // Additional structured data
}

/**
 * Union type for attachments - can be a neutral upload {@link Asset}, URL
 * attachment, or data attachment. Uses `Asset` (not the web `File`) so the
 * compose/send state machine stays platform-neutral (ELARA-692).
 */
export type ChatAttachment = Asset | UrlAttachment | DataAttachment;

/**
 * Type guard to check if an attachment is a URL attachment
 */
export function isUrlAttachment(attachment: ChatAttachment): attachment is UrlAttachment {
  return (
    typeof attachment === 'object' &&
    attachment !== null &&
    'type' in attachment &&
    attachment.type === 'url' &&
    'url' in attachment
  );
}

/**
 * Type guard to check if an attachment is an uploadable {@link Asset} (as
 * opposed to a URL or data reference).
 */
export function isAssetAttachment(attachment: ChatAttachment): attachment is Asset {
  return typeof attachment === 'object' && attachment !== null && !isUrlAttachment(attachment) && !isDataAttachment(attachment);
}

/**
 * Type guard to check if an attachment is a data attachment
 */
export function isDataAttachment(attachment: ChatAttachment): attachment is DataAttachment {
  return (
    typeof attachment === 'object' &&
    attachment !== null &&
    'type' in attachment &&
    'id' in attachment
  );
}

/** A live composer draft, before it is filtered down to the stored {@link StoredChatDraft} shape. */
type DraftSnapshot = {
  message: string;
  answeredQuestions: Record<string, string | string[]>;
  attachments: ChatAttachment[];
};

function serializableAttachments(attachments: ChatAttachment[]): Array<UrlAttachment | DataAttachment> {
  return attachments.filter(
    (attachment): attachment is UrlAttachment | DataAttachment =>
      isUrlAttachment(attachment) || isDataAttachment(attachment),
  );
}

function draftHasContent(draft: DraftSnapshot): boolean {
  return (
    draft.message.trim().length > 0 ||
    serializableAttachments(draft.attachments).length > 0 ||
    Object.keys(draft.answeredQuestions).length > 0
  );
}

/** Callers guarantee the draft is non-empty; an empty one is cleared, not saved. */
function saveDraftSnapshot(
  keyValueStore: KeyValueStore,
  chatId: string,
  draft: DraftSnapshot,
  tripId: string | undefined,
): void {
  saveChatDraft(
    keyValueStore,
    chatId,
    {
      message: draft.message,
      answeredQuestions: draft.answeredQuestions,
      attachments: serializableAttachments(draft.attachments),
    },
    tripId,
  );
}

/**
 * Minimal focusable-input contract (ELARA-754). The chat input registers its underlying text field
 * so `focusInput()` can refocus it — web passes an `HTMLTextAreaElement`, React Native a `TextInput`
 * ref. Both expose `focus()`, so depending on this neutral shape keeps the store off the DOM
 * `HTMLTextAreaElement` type. The web Speech Recognition DOM types now live in the web port
 * implementation (`lib/web-speech-recognition-port.ts`).
 */
export interface FocusableInput {
  focus(): void;
}

/**
 * Minimal scroll seam ChatInputState needs from the message view (ELARA-754). After sending a
 * message the input scrolls the conversation to the bottom. The concrete scroller is web-only
 * (`MessageViewState` drives DOM scroll containers and stays in `@elara/client`), so the portable
 * input depends on this one-method contract instead of the DOM-coupled store.
 */
export interface MessageScroller {
  scrollToBottom(): void;
}

/**
 * Minimal key-press contract for the composer's Enter handling (ELARA-754). Web passes a React
 * `KeyboardEvent`; both it and a native key event expose these members, so depending on this neutral
 * shape keeps the store off the DOM `KeyboardEvent` type.
 */
export interface KeyPressLike {
  key: string;
  shiftKey: boolean;
  preventDefault(): void;
}

/**
 * The main reactive store for chat input functionality
 *
 * @remark All the getters in FlatStore are transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
export function createChatInputState(
  /**
   * The chat this composer belongs to. `undefined` for the id-less sentinel instance used while no
   * chat is selected (e.g. `/trip/new`); persistence is skipped when absent since there is no key.
   */
  chatId: string | undefined,
  chatsState: ChatsState,
  messageViewState: MessageScroller,
  fileUploadState: FileUploadState,
  tripsState: TripsState,
  modelState: ModelState,
  keyValueStore: KeyValueStore,
  filePicker: FilePicker,
  imageProcessor: ImageProcessor,
  speechRecognition: SpeechRecognitionPort,
) {
  let textareaRef: FocusableInput | undefined;
  let cleanupFunctionRef: (() => void) | null = null;
  let typingTimeoutRef: ReturnType<typeof setTimeout> | null = null;
  /** Trailing-edge save timer, plus the latest edit waiting on it (ELARA-968). */
  let draftSaveTimeoutRef: ReturnType<typeof setTimeout> | null = null;
  let pendingDraft: DraftSnapshot | undefined;
  let lastSubmittedDraft: DraftSnapshot | undefined;
  let sendGeneration = 0;

  /** Drops the trailing-edge timer and any edit waiting on it, without writing. */
  function cancelPendingSave(): void {
    if (draftSaveTimeoutRef) {
      clearTimeout(draftSaveTimeoutRef);
      draftSaveTimeoutRef = null;
    }
    pendingDraft = undefined;
  }

  /** Called when monitors stop (chat switch, unmount) — nothing else will fire this timer. */
  function flushPendingDraft(): void {
    const draft = pendingDraft;
    cancelPendingSave();
    if (draft && chatId) saveDraftSnapshot(keyValueStore, chatId, draft, tripsState.current?.id);
  }

  /** Leading + trailing edge debounce over draft writes ({@link DRAFT_SAVE_DEBOUNCE_MS}). */
  function scheduleDraftSave(draft: DraftSnapshot): void {
    if (!chatId) return;
    // An emptied draft (e.g. cleared after a send) drops its key immediately: waiting out the window
    // would leave a stale draft readable, and a coalesced edit must not resurrect it.
    if (!draftHasContent(draft)) {
      cancelPendingSave();
      clearChatDraft(keyValueStore, chatId);
      return;
    }
    // Mid-burst: coalesce onto the running timer.
    if (draftSaveTimeoutRef) {
      pendingDraft = draft;
      return;
    }
    // Leading edge: save now, then open the quiet window.
    saveDraftSnapshot(keyValueStore, chatId, draft, tripsState.current?.id);
    draftSaveTimeoutRef = setTimeout(() => {
      draftSaveTimeoutRef = null;
      flushPendingDraft();
    }, DRAFT_SAVE_DEBOUNCE_MS);
  }

  // Hydrate the persisted draft (text + answered questions + URL/data attachments) for this chat so an
  // unsent draft survives a reload. Absent/corrupt storage yields blank defaults.
  const persisted = chatId ? loadChatDraft(keyValueStore, chatId) : undefined;

  // Shuffle examples on each page load so the order feels fresh
  const PLACEHOLDER_EXAMPLES = [
    "Help me plan a family getaway to the Amalfi Coast...",
    "What’s the best family-friendly hotel in Kyoto...",
    "Where in Europe could we go for a weekend break...",
    "Where should we go for spring break...",
    "Find me a room at the Conrad hotel in London next week...",
    "Help me plan a surprise trip for my partner’s birthday...",
    "What flights are available to Sydney next Monday..."
  ].toSorted(() => Math.random() - 0.5);
  const PLACEHOLDER_CYCLE_MS = 4000;

  let placeholderIntervalRef: ReturnType<typeof setInterval> | undefined;

  const state = createState({
    /** The current message text */
    message: persisted?.message ?? '',
    /** Attachments attached to the current message (assets, URLs, or data refs) */
    attachments: (persisted?.attachments ?? []) as ChatAttachment[],
    /** Question answers to be submitted with the message (single string or array for multi-select) */
    answeredQuestions: (persisted?.answeredQuestions ?? {}) as Record<string, string | string[]>,
    /** Whether a message is currently being sent */
    isSending: false,
    /** Whether files are being dragged over the input area */
    isDragging: false,
    /** Whether the user is currently typing */
    isTyping: false,
    /** Whether voice input is currently active */
    isVoiceInputActive: false,
    /** Pending voice text from voice input (what user is currently saying) */
    pendingVoiceText: '',
    /** Current index for the cycling placeholder examples */
    placeholderIndex: 0,

    /** Whether the platform supports speech recognition (via the injected port) */
    get supportsVoiceInput(): boolean {
      return speechRecognition.isSupported;
    },
    get currentChat(): Chat | undefined {
      return chatsState.current;
    },
    /**
     * Whether the current trip is a demo trip. Demo trips are read-only: we
     * want users to start a fresh trip rather than continue chatting in the
     * seeded demo, so the input is disabled and a call-to-action is shown.
     */
    get isDemoTrip(): boolean {
      return tripsState.current?.isDemo ?? false;
    },
    /** Whether the input is disabled */
    get isInputDisabled(): boolean {
      // Guard: Check if chatsState and current chat are available
      return this.isSending || this.isDragging || this.isDemoTrip || (this.currentChat?.isArchived ?? false);
    },
    /** Whether the chat is empty (no messages yet) — used for cycling placeholder and, on mobile, the
     * composer's starter-suggestion chips. `isSelectionSettled` first — see its doc comment — so a
     * trip/chat switch still resolving doesn't read as a confirmed-empty chat (ELARA-738/739). */
    get isChatEmpty(): boolean {
      return chatsState.isSelectionSettled && chatsState.messages.isReady && chatsState.messagesList.length === 0;
    },
    /** The placeholder text for the input */
    get placeholder(): string {
      if (this.hasAnsweredQuestions) return 'Add more details...';
      if (this.isChatEmpty) return PLACEHOLDER_EXAMPLES[this.placeholderIndex] ?? 'Ask anything...';
      return 'Ask anything...';
    },
    /** Get the current display text (message + pending voice text) */
    get displayText(): string {
      return this.message + this.pendingVoiceText;
    },
    /** Error message if send operation fails */
    sendError: undefined as string | undefined,
    /** Check if the input can be sent (has content, not already sending, and within the attachment size limit) */
    get canSend(): boolean {
      return this.hasUnsavedChanges && !this.isSending && !this.attachmentsExceedLimit;
    },
    /**
     * Check if there are unsaved changes (any message text or attachments). Reads `displayText`
     * (message + live dictation), not just `message` — otherwise the composer's send affordance
     * (and `canSend`) stay stuck on "empty" while text is only sitting in `pendingVoiceText`, not yet
     * folded in by the recognizer's own final-transcript event.
     */
    get hasUnsavedChanges(): boolean {
      return this.displayText.trim().length > 0 || this.attachments.length > 0 || this.hasAnsweredQuestions;
    },
    /** Get total size of all attachments */
    get totalAttachmentSize(): number {
      return this.attachments.reduce((total, attachment) => {
        if (isUrlAttachment(attachment)) {
          return total + (attachment.size ?? 0);
        }
        if (isDataAttachment(attachment)) {
          // Data attachments don't have size
          return total;
        } else {
          // Asset attachment
          return total + attachment.size;
        }
      }, 0);
    },
    /** Check if attachments exceed size limit (shared with the server-enforced cap) */
    get attachmentsExceedLimit(): boolean {
      return this.totalAttachmentSize > MAX_ATTACHMENT_BYTES;
    },
    /** Get formatted total attachment size */
    get formattedAttachmentSize(): string {
      const bytes = this.totalAttachmentSize;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    /** Updates the message text */
    setMessage(text: string) {
      this.message = text;
      this.sendError = undefined; // Clear error when user types
      // Set typing state to true when user types
      this.isTyping = true;
      // Clear existing timeout
      if (typingTimeoutRef) {
        clearTimeout(typingTimeoutRef);
        typingTimeoutRef = null;
      }
      // Set timeout to mark as not typing after 1.5 seconds of inactivity
      typingTimeoutRef = setTimeout(() => {
        this.isTyping = false;
        typingTimeoutRef = null;
      }, 1500);
    },
    /** Adds assets to the attachments list */
    addAttachments(assets: Asset[]) {
      this.attachments = [...this.attachments, ...assets];
      this.sendError = undefined; // Clear error when adding files
    },
    /**
     * Opens the platform file picker and appends the chosen assets. The accept
     * filter mirrors the server-enforced attachment types. Selection failures
     * surface as `sendError` rather than throwing.
     */
    async pickFiles() {
      try {
        const assets = await filePicker.pick({ accept: 'image/*,application/pdf,text/*', multiple: true });
        if (assets.length > 0) this.addAttachments(assets);
      } catch (error) {
        console.error('[ChatInputState] Failed to pick files:', error);
        this.sendError = 'Unable to add files. Please try again.';
      }
    },
    /** Adds a URL attachment */
    addUrlAttachment(url: string, mimeType: string, name: string, size?: number) {
      // Validate URL
      try {
        const urlObj = new URL(url);
        if (!['http:', 'https:'].includes(urlObj.protocol)) {
          throw new Error('Only HTTP/HTTPS URLs are allowed');
        }
      } catch (error) {
        console.error('[ChatInputState] Invalid URL:', error);
        this.sendError = 'Invalid image URL';
        return;
      }

      this.attachments = [...this.attachments, { url, mimeType, name, size, type: 'url' } as UrlAttachment];
      this.sendError = undefined; // Clear error when adding attachment
    },
    /** Adds a generic data attachment (destination, hotel, or any other structured data) */
    addDataAttachment(type: DataAttachmentType, id: string, displayName?: string, data?: Record<string, unknown>) {
      if (!id?.trim()) throw new Error('ID is required for data attachments');

      if (this.attachments.some((attachment) => isDataAttachment(attachment) && attachment.type === type && attachment.id === id)) {
        return;
      }

      this.attachments = [...this.attachments, { type, id, displayName, data } as DataAttachment];
      this.sendError = undefined; // Clear error when adding attachment
    },
    /** Removes a specific attachment */
    removeAttachment(index: number) {
      this.attachments = this.attachments.filter((_, i) => i !== index);
    },
    /** Clears all attachments */
    clearAttachments() {
      this.attachments = [];
    },
    /** Sets the drag state */
    setDragging(isDragging: boolean) {
      this.isDragging = isDragging;
    },
    /**
     * Clears input content without affecting sending state.
     * Used during sendMessage() to clear the input while keeping isSending true.
     */
    clearInput() {
      this.message = '';
      this.attachments = [];
      this.answeredQuestions = {};
      this.isTyping = false;
      // Clear typing timeout
      if (typingTimeoutRef) {
        clearTimeout(typingTimeoutRef);
        typingTimeoutRef = null;
      }
      this.pendingVoiceText = '';
    },
    /** Returns an early-stopped send to this chat's existing composer. */
    restoreEarlyStoppedDraft(fallbackText: string) {
      const draft = lastSubmittedDraft;
      lastSubmittedDraft = undefined;
      sendGeneration += 1;
      this.isSending = false;
      this.message = draft?.message ?? fallbackText;
      if (draft) {
        this.attachments = draft.attachments;
        this.answeredQuestions = draft.answeredQuestions;
      }
      this.sendError = undefined;
      this.focusInput();
    },
    /** Resets all input state (used when switching trips) */
    reset() {
      this.clearInput();
      this.sendError = undefined;
      this.isSending = false;
      this.isDragging = false;
      // Stop voice input if active
      if (this.isVoiceInputActive) {
        this.stopVoiceInput();
      }
    },
    /** Sends the current message with attachments */
    async sendMessage() {
      if (this.attachmentsExceedLimit) {
        this.sendError = 'Total attachment size exceeds 25MB limit';
        return;
      }
      if (!this.canSend || !chatsState.current) {
        console.warn('[ChatInputState] Cannot send: no content or no active chat');
        return;
      }
      messageViewState.scrollToBottom();

      const currentSendGeneration = ++sendGeneration;
      this.isSending = true;
      this.sendError = undefined;
      // Backup input state for restoration on error. `displayText`, not `message`: sending while
      // dictation is still live must include whatever's currently shown, not just the portion the
      // recognizer has already finalized into `message` — otherwise the trailing, still-interim words
      // are silently dropped from the sent content.
      const currentMessage = this.displayText;
      const currentAttachments = [...this.attachments];
      const currentAnsweredQuestions = { ...this.answeredQuestions };
      // Tracks whether we've handed the message off to the server (past attachment upload / content
      // building). Failures before this point (e.g. an attachment upload error) never reached the
      // server, so the draft must always be restored — see the catch block (ELARA-869).
      let dispatched = false;
      try {
        // Build message content
        const content: Array<TextPart | (MediaPart & { thumbnail?: string }) | DataPart> = [];
        // Add text content if present
        if (currentMessage.trim()) {
          content.push({ text: currentMessage.trim() });
        }

        // Process attachments if present
        if (currentAttachments.length > 0) {
          // Separate attachments by type
          const hasAssetAttachments = currentAttachments.some(isAssetAttachment);

          // Fail fast if we have asset attachments but no storage path yet
          if (hasAssetAttachments && !chatsState.attachmentStoragePath) {
            this.sendError = 'Unable to upload attachments. Please try again in a moment.';
            this.isSending = false;
            return;
          }

          const assets = currentAttachments.filter(isAssetAttachment);
          const urlAttachments = currentAttachments.filter(isUrlAttachment);
          const dataAttachments = currentAttachments.filter(isDataAttachment);

          // Upload asset attachments
          if (assets.length > 0 && chatsState.attachmentStoragePath) {
            const uploadResults = await fileUploadState.uploadFiles(assets, chatsState.attachmentStoragePath);
            // Add media parts for uploaded assets
            for (const { asset, url } of uploadResults) {
              const thumbnail = asset.mimeType.startsWith('image/')
                ? await imageProcessor.generateThumbnail(asset)
                : undefined;
              content.push({ media: { url, contentType: asset.mimeType }, thumbnail });
            }
          }

          // Add URL attachments directly (no upload needed)
          for (const urlAttachment of urlAttachments) {
            content.push({ media: { url: urlAttachment.url, contentType: urlAttachment.mimeType } });
          }

          // Add data attachments as DataPart
          for (const dataAttachment of dataAttachments) {
            content.push({
              data: {
                type: dataAttachment.type,
                id: dataAttachment.id,
                displayName: dataAttachment.displayName,
                ...dataAttachment.data, // Include any additional data
              },
            });
          }
        }
        if (Object.keys(this.answeredQuestions).length > 0) {
          content.push({ data: { type: 'answeredQuestions', answeredQuestions: this.answeredQuestions } });
        }
        // Stop voice input if active
        if (this.isVoiceInputActive) {
          this.stopVoiceInput();
        }
        lastSubmittedDraft = {
          message: currentMessage,
          attachments: currentAttachments,
          answeredQuestions: currentAnsweredQuestions,
        };
        // Clear input immediately for better UX (keeps isSending=true to prevent duplicate sends)
        this.clearInput();
        // Capture ids before the await so the analytics event is unaffected if `current` changes mid-send.
        const sentChatId = chatsState.current.id;
        const sentTripId = tripsState.current?.id;
        // Handing the message to the server: everything after this point (the request, the reply
        // stream) can fail *after* the server has already persisted the message.
        dispatched = true;
        // Send the message
        await chatsState.current.sendMessage(
          {
            role: 'user',
            userId: chatsState.current.userId,
            chatId: chatsState.current.id,
            content,
            sequence: chatsState.nextMessageSequence(),
          },
          {
            abortSignal: chatsState.abortController.signal,
            modelId: modelState.modelOverride,
            promptVersion: modelState.promptVersionOverride,
            reasoningEffort: modelState.reasoningEffortOverride,
            skillVersions: modelState.skillVersionsOverride,
          },
        );
        trackMessageSent({
          tripId: sentTripId,
          chatId: sentChatId,
          hasAttachments: currentAttachments.length > 0,
        });
      } catch (error) {
        // A user Stop / teardown aborts the in-flight request. The message was genuinely sent, so
        // this is not a failure: don't surface an error and don't restore the composer input.
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        if (dispatched && isUnreachableFlowError(error)) {
          // Expected when the app is backgrounded mid-send and iOS tears the request down: the server
          // still finishes the reply, which lands through the chat listener. An error log here only
          // raised the dev build's error banner.
          console.info('[ChatInputState] Send connection dropped after dispatch:', error);
          return;
        }
        console.error('[ChatInputState] Failed to send message:', error);
        if (dispatched && !isPreDeliveryRejection(error)) {
          // The message reached the server and the failure is downstream — the AI reply stream errored
          // (a model/server error), or the connection dropped after the send (e.g. the app was
          // backgrounded the instant after tapping send, suspending the in-flight request). Either way
          // `SendMessageFlow` persists the user's message *before* it streams the reply, so the message
          // is already in the chat feed. Leaving the composer cleared is correct: restoring the draft
          // here left stale text stuck in the input and invited a duplicate send (ELARA-869). The
          // reply's own error surfaces inline in the conversation.
          return;
        }
        // The message never reached the server (an attachment upload failed, or it was rejected at the
        // boundary before being persisted) — restore the draft so the user can retry without retyping.
        this.message = currentMessage;
        this.attachments = currentAttachments;
        this.answeredQuestions = currentAnsweredQuestions;
        this.sendError = formatSendErrorMessage(error);
      } finally {
        if (sendGeneration === currentSendGeneration) this.isSending = false;
      }
    },

    /** Handles Enter key press (send on Enter, newline on Shift+Enter) */
    async handleEnterKey(event: KeyPressLike) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        await this.sendMessage();
      }
    },
    /** Formats file size for display */
    formatFileSize(bytes: number): string {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    /** Registers the textarea ref for external focus control */
    registerTextareaRef(ref: FocusableInput | undefined) {
      textareaRef = ref;
    },
    /** Focuses the chat input textarea */
    focusInput() {
      textareaRef?.focus();
    },
    _session: undefined as SpeechRecognitionSession | undefined,
    /** Starts voice input via the injected SpeechRecognitionPort */
    startVoiceInput() {
      if (!this.supportsVoiceInput || this.isVoiceInputActive || this.isInputDisabled) {
        return;
      }

      this._session = speechRecognition.start({
        onStart: () => {
          this.isVoiceInputActive = true;
        },
        onInterimTranscript: (text) => {
          // Update pending voice text for real-time display
          this.pendingVoiceText = text;
        },
        onFinalTranscript: (text) => {
          // Add final transcript to the message
          this.setMessage(this.message + text);
          this.pendingVoiceText = '';
        },
        onError: (error) => {
          console.error('Speech recognition error:', error);
          this.stopVoiceInput();
        },
        onEnd: () => {
          this.isVoiceInputActive = false;
          // Normally `onFinalTranscript` has already folded any trailing dictation into `message` and
          // cleared this by the time `end` fires. But recognizers don't universally guarantee a final
          // result for content still mid-utterance when the session ends (e.g. a silence timeout) —
          // without this, that text would vanish from view the instant dictation stops, even though
          // the user can see it sitting in the input right up to that point.
          if (this.pendingVoiceText) {
            this.setMessage(this.message + this.pendingVoiceText);
          }
          this.pendingVoiceText = '';
        },
      });
    },
    /** Stops voice input */
    stopVoiceInput() {
      if (!this.isVoiceInputActive) {
        return;
      }

      this._session?.stop();
      this._session = undefined;
      this.isVoiceInputActive = false;
      this.pendingVoiceText = '';
    },
    /** Toggles voice input on/off */
    toggleVoiceInput() {
      if (this.isVoiceInputActive) {
        this.stopVoiceInput();
      } else {
        this.startVoiceInput();
      }
    },
    get questions(): UserQuestion[] {
      // const lastMessage = chatsState.lastMessage;
      // if (!lastMessage || !lastMessage.isModelMessage || !lastMessage.content) {
      //   return [];
      // }

      // Look for AskUserWidget in the message content
      // for (const part of lastMessage.content) {
      //   if (isWidgetPart(part) && part.widget.type === 'AskUserWidget') {
      //     const data = part.widget.payload?.data;
      //     if (data && typeof data === 'object' && 'questions' in data && Array.isArray(data.questions)) {
      //       return data.questions as UserQuestion[];
      //     }
      //   }
      // }

      return [];
    },
    get hasAnsweredQuestions(): boolean {
      return Object.keys(this.answeredQuestions).length > 0;
    },
    /**
     * Answers a question (single-select mode).
     * For multi-select, use toggleAnswer instead.
     */
    answerQuestion({ question, answer }: { question: string; answer: string }) {
      if (!answer || answer.trim().length === 0) return;
      this.answeredQuestions = { ...this.answeredQuestions, [question.trim()]: answer.trim() };
    },
    /**
     * Toggles an answer for multi-select questions.
     * If answer is already selected, removes it. Otherwise, adds it.
     */
    toggleAnswer({ question, answer }: { question: string; answer: string }) {
      if (!answer || answer.trim().length === 0) return;
      const key = question.trim();
      const value = answer.trim();
      const current = this.answeredQuestions[key];

      let newAnswers: string[];
      if (Array.isArray(current)) {
        if (current.includes(value)) {
          // Remove the answer
          newAnswers = current.filter((a) => a !== value);
        } else {
          // Add the answer
          newAnswers = [...current, value];
        }
      } else if (current === value) {
        // Single value that matches - convert to empty array (toggle off)
        newAnswers = [];
      } else if (current) {
        // Single value that doesn't match - create array with both
        newAnswers = [current, value];
      } else {
        // No current value - create array with just this answer
        newAnswers = [value];
      }

      if (newAnswers.length === 0) {
        this.answeredQuestions = omit(this.answeredQuestions, key);
      } else {
        this.answeredQuestions = { ...this.answeredQuestions, [key]: newAnswers };
      }
    },
    /**
     * Check if a specific answer is selected for a question.
     * Works for both single-select and multi-select.
     */
    isAnswerSelected(question: string, answer: string): boolean {
      const current = this.answeredQuestions[question.trim()];
      if (Array.isArray(current)) {
        return current.includes(answer.trim());
      }
      return current === answer.trim();
    },
    getAnswer(question: string): string | string[] | undefined {
      return this.answeredQuestions[question.trim()];
    },
    removeAnswer(question: string) {
      this.answeredQuestions = omit(this.answeredQuestions, question.trim());
    },
    clearAllAnswers() {
      this.answeredQuestions = {};
    },
    /** Start cycling placeholder examples */
    startPlaceholderCycle() {
      if (placeholderIntervalRef) return;
      this.placeholderIndex = 0;
      placeholderIntervalRef = setInterval(() => {
        this.placeholderIndex = (this.placeholderIndex + 1) % PLACEHOLDER_EXAMPLES.length;
      }, PLACEHOLDER_CYCLE_MS);
    },
    /** Stop cycling placeholder examples */
    stopPlaceholderCycle() {
      if (placeholderIntervalRef) {
        clearInterval(placeholderIntervalRef);
        placeholderIntervalRef = undefined;
      }
    },
    /**
     * Start this chat's composer monitors (idempotent - safe to call multiple times). The manager
     * ({@link createChatInputStateManager}) calls this only while this instance is the active chat, so
     * the placeholder timer and persistence effect run for the on-screen composer alone. Cross-chat
     * concerns (reset-on-switch, model sync, guide-seed handoff) are the manager's job now — this
     * instance is dedicated to one chat and never resets itself out from under a draft.
     */
    startMonitors() {
      // Return existing cleanup function if already started
      if (cleanupFunctionRef) return cleanupFunctionRef;

      // Register effect to start/stop placeholder cycling based on chat empty state
      const placeholderCleanup = effect(() => {
        if (this.isChatEmpty) {
          this.startPlaceholderCycle();
        } else {
          this.stopPlaceholderCycle();
        }
      });

      // Save the draft as the composer changes, so it survives a reload. Debounced (ELARA-968) and
      // skipped for the id-less sentinel instance, which has no key.
      const persistCleanup = effect(() => {
        const message = this.message;
        const attachments = this.attachments;
        const answeredQuestions = this.answeredQuestions;
        if (!chatId) return;
        scheduleDraftSave({ message, attachments, answeredQuestions });
      });

      cleanupFunctionRef = () => {
        placeholderCleanup();
        persistCleanup();
        flushPendingDraft();
        this.stopPlaceholderCycle();
      };
      return cleanupFunctionRef;
    },
    /**
     * Stop this chat's composer monitors. Stopping monitors means this chat is no longer the active,
     * on-screen composer — the manager calls this on the outgoing instance when you switch chats, and
     * on every instance at teardown — so it also ends any live voice dictation and clears the pending
     * typing timer. Without this, dictating in one chat and switching away would leave the microphone
     * open with transcripts firing into the now-off-screen chat's signals until it was revisited or
     * LRU-evicted (ELARA-952 review). `stopVoiceInput()` is a no-op when no session is active.
     */
    stopMonitors() {
      if (cleanupFunctionRef) {
        cleanupFunctionRef();
        cleanupFunctionRef = null;
      }
      this.stopVoiceInput();
      if (typingTimeoutRef) {
        clearTimeout(typingTimeoutRef);
        typingTimeoutRef = null;
      }
    },
    /**
     * Fully tear down this instance before the manager evicts it. `stopMonitors()` already ends voice
     * dictation and the typing timer; this additionally stops the placeholder cycle, which can run even
     * when monitors were never started. Draft content is already persisted, so eviction loses nothing.
     */
    dispose() {
      this.stopMonitors();
      this.stopPlaceholderCycle();
    },
  });
  return state;
}

export type ChatInputState = ReturnType<typeof createChatInputState>;

/**
 * Flow error codes that mean the server rejected the message at the boundary, *before* it was
 * persisted. `SendMessageFlow` runs every input-validation / authorization check before it writes the
 * user's message, so an "expected business" rejection (these codes) means the message never landed.
 * Mirrors the server's `EXPECTED_BUSINESS_ERROR_CODES` (`server/src/utils/implementFlow.ts`).
 */
const PRE_DELIVERY_REJECTION_CODES = new Set<string>([
  'invalid_argument',
  'not_found',
  'permission_denied',
  'unauthenticated',
  'conflict',
]);

/**
 * Whether a failed send was rejected by the server *before* the message was persisted — the only case
 * where the composer draft should be restored (ELARA-869). Everything else (a transport error, a
 * dropped/suspended connection from backgrounding, or an internal/model error) happens *after*
 * `SendMessageFlow` has persisted the user's message, so the message is already in the feed and the
 * draft must stay cleared.
 *
 * Reads the flow error classification carried by both error shapes that can reach the send catch: a
 * `FlowHttpError` from an SSE `error` event exposes the server's `code`; a `DetailedError` built from
 * a `{ failed: true }` stream chunk carries `flowErrorClass` / `flowErrorCode` (assigned as own
 * properties, and also nested under `details`). A raw transport error (e.g. a suspended fetch) carries
 * none of these and is therefore treated as "delivered".
 */
export function isPreDeliveryRejection(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const record = error as {
    code?: unknown;
    flowErrorClass?: unknown;
    flowErrorCode?: unknown;
    details?: unknown;
  };
  const hasBusinessCode = (value: unknown): boolean =>
    typeof value === 'string' && PRE_DELIVERY_REJECTION_CODES.has(value);

  if (hasBusinessCode(record.code)) return true;
  if (record.flowErrorClass === 'expected_business') return true;
  if (hasBusinessCode(record.flowErrorCode)) return true;

  if (typeof record.details === 'object' && record.details !== null) {
    const details = record.details as { flowErrorClass?: unknown; flowErrorCode?: unknown };
    if (details.flowErrorClass === 'expected_business') return true;
    if (hasBusinessCode(details.flowErrorCode)) return true;
  }
  return false;
}

export function formatSendErrorMessage(error: unknown): string {
  const fallbackMessage = 'Failed to send message';
  const baseMessage = errorMessage(error, { fallbackMessage });

  if (
    typeof error === 'object' &&
    error !== null &&
    'originalMessage' in error &&
    typeof (error as { originalMessage?: unknown }).originalMessage === 'string'
  ) {
    const original = (error as { originalMessage: string }).originalMessage.trim();
    if (original) return original;
  }

  if (typeof error === 'object' && error !== null && 'details' in error) {
    const details = (error as { details?: unknown }).details;
    const extracted = extractMessageFromDetails(details);
    if (extracted) return extracted;
  }

  if (baseMessage.trim().toUpperCase() === 'INTERNAL') {
    const extracted = extractMessageFromDetails(error);
    if (extracted) return extracted;
  }

  return baseMessage;
}

function extractMessageFromDetails(details: unknown): string | undefined {
  if (!details) return;
  if (typeof details === 'string') {
    const normalized = details.trim();
    return normalized.length > 0 ? normalized : undefined;
  }
  if (typeof details !== 'object') return;

  const record = details as Record<string, unknown>;
  const candidateKeys = ['message', 'error', 'errorMessage', 'description'];
  for (const key of candidateKeys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  if ('streamError' in record) {
    const nested = extractMessageFromDetails(record.streamError);
    if (nested) return nested;
  }
  if ('persistenceError' in record) {
    const nested = extractMessageFromDetails(record.persistenceError);
    if (nested) return nested;
  }

  for (const value of Object.values(record)) {
    const nested = extractMessageFromDetails(value);
    if (nested) return nested;
  }

  return;
}
